import { NavLink, Outlet, useNavigate } from 'react-router';

import { useTema } from '@/app/tema/useTema';
import { Logo } from '@/components/Logo/Logo';
import { useSessao } from '@/features/autenticacao/useSessao';
import type { Tema } from '@/lib/preferencias';
import { NOME_DO_PRODUTO } from '@/lib/produto';

import estilos from './LayoutPrincipal.module.css';

/**
 * Casca da aplicacao.
 *
 * A navegacao por perfil (item 7.8) entra junto com as telas de cada painel. O
 * que existe aqui hoje e comum a todos os perfis.
 */

export function LayoutPrincipal() {
  const { tema, definirTema } = useTema();
  const { usuario, sair, temPermissao } = useSessao();
  const navegar = useNavigate();

  /*
    Os links de administracao abaixo aparecem so para quem tem a permissao.

    ISSO E DESENHO DE INTERFACE, NAO PROTECAO. O servidor recusa com 403 de qualquer
    forma, e quem digitar /usuarios na barra de endereco vai ver a tela carregar e
    falhar. Esconder o link evita oferecer o que nao funciona.
  */

  const encerrar = async () => {
    await sair();

    void navegar('/login', { replace: true });
  };

  return (
    <div className={estilos.casca}>
      <header className={estilos.cabecalho}>
        <div className={estilos.marca}>
          {/* alt vazio: o nome do produto esta escrito ao lado, e repetir
              geraria leitura duplicada no leitor de tela. */}
          <Logo variante="positiva" altura={28} textoAlternativo="" />
          <span className={estilos.divisor} aria-hidden="true" />
          <span className={estilos.nomeDoProduto}>{NOME_DO_PRODUTO}</span>
        </div>

        <nav className={estilos.navegacao} aria-label="Navegação principal">
          {/*
            OS DOIS PAINEIS APARECEM CONFORME A PERMISSAO.

            `painel.cs` e `painel.produto` existem desde o inicio e nunca eram consultadas:
            havia uma tela so, igual para os dois lados. Quem tem as duas — Admin, Gestor
            de Produto — ve as duas abas, e isso e proposital: o Gestor de Produto precisa
            olhar o que o CS pediu.
          */}
          {temPermissao('painel.cs') && (
            <NavLink
              to="/acoes"
              className={({ isActive }) =>
                isActive ? `${estilos.link} ${estilos.linkAtivo}` : estilos.link
              }
            >
              Painel CS
            </NavLink>
          )}

          {temPermissao('painel.produto') && (
            <NavLink
              to="/fila"
              className={({ isActive }) =>
                isActive ? `${estilos.link} ${estilos.linkAtivo}` : estilos.link
              }
            >
              Painel Produto
            </NavLink>
          )}

          {/*
            UM LINK PARA OS TRES CADASTROS.

            Areas, Clientes e Usuarios eram links de primeiro nivel ao lado dos paineis,
            disputando espaco com o trabalho do dia. Cadastro e operacao ocasional de
            administrador, e as tres permissoes sao exclusivas dele.

            A condicao aceita qualquer uma das tres em vez de perguntar se o perfil e Admin:
            se amanha uma delas voltar para outro perfil, o link aparece sem que ninguem
            precise lembrar de mexer aqui.
          */}
          {(temPermissao('area.gerenciar') ||
            temPermissao('cliente.gerenciar') ||
            temPermissao('usuario.gerenciar')) && (
            <NavLink
              to="/cadastros"
              className={({ isActive }) =>
                isActive ? `${estilos.link} ${estilos.linkAtivo}` : estilos.link
              }
            >
              Cadastros
            </NavLink>
          )}

          <NavLink
            to="/diagnostico"
            className={({ isActive }) =>
              isActive ? `${estilos.link} ${estilos.linkAtivo}` : estilos.link
            }
          >
            Diagnóstico
          </NavLink>
          <NavLink
            to="/tokens"
            className={({ isActive }) =>
              isActive ? `${estilos.link} ${estilos.linkAtivo}` : estilos.link
            }
          >
            Tokens
          </NavLink>
        </nav>

        <SeletorDeTema tema={tema} aoDefinir={definirTema} />

        {usuario !== null && (
          <div className={estilos.usuario}>
            {/* O perfil aparece junto do nome porque o mesmo usuario pode estranhar
                nao ver um botao que um colega ve. Sem o perfil visivel, a diferenca
                parece defeito. */}
            <span className={estilos.nomeDoUsuario}>{usuario.nome}</span>
            <span className={estilos.perfil}>{usuario.perfil}</span>
            <button
              type="button"
              className={estilos.botaoDeSair}
              onClick={() => {
                void encerrar();
              }}
            >
              Sair
            </button>
          </div>
        )}
      </header>

      <Outlet />
    </div>
  );
}

interface SeletorDeTemaProps {
  tema: Tema;
  aoDefinir: (tema: Tema) => void;
}

function SeletorDeTema({ tema, aoDefinir }: SeletorDeTemaProps) {
  const opcoes: { valor: Tema; rotulo: string; simbolo: string }[] = [
    { valor: 'claro', rotulo: 'Tema claro', simbolo: '\u2600' },
    { valor: 'escuro', rotulo: 'Tema escuro', simbolo: '\u263D' },
    { valor: 'sistema', rotulo: 'Seguir o sistema', simbolo: '\u25D1' },
  ];

  return (
    <div className={estilos.seletorDeTema} role="group" aria-label="Tema da interface">
      {opcoes.map((opcao) => (
        <button
          key={opcao.valor}
          type="button"
          className={estilos.botaoDeTema}
          aria-pressed={tema === opcao.valor}
          title={opcao.rotulo}
          onClick={() => {
            aoDefinir(opcao.valor);
          }}
        >
          <span aria-hidden="true">{opcao.simbolo}</span>
          <span className="apenas-leitor-de-tela">{opcao.rotulo}</span>
        </button>
      ))}
    </div>
  );
}
